/* ============================================================
   ConfirmerEmail — page PUBLIQUE de confirmation d'un changement d'e-mail
   ------------------------------------------------------------
   Cible du lien envoyé à la NOUVELLE adresse (/confirmer-email?token=…).

   Volontairement publique : celui qui relève l'e-mail n'est pas forcément
   connecté sur cet appareil. La preuve d'identité, c'est le jeton — à usage
   unique et valable 1 h côté backend.
   ============================================================ */
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../config/api';

export default function ConfirmerEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['auth', 'common']);
  const [state, setState] = useState({ status: 'loading', email: null, error: null });
  // React 18+ monte les effets deux fois en développement : sans ce garde, le
  // jeton (à usage unique) serait consommé par le premier appel et le second
  // afficherait « lien expiré » à tort.
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;

    const token = searchParams.get('token');
    if (!token) {
      setState({ status: 'error', email: null, error: 'missing_token' });
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/account/confirm-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok && data.success) {
          setState({ status: 'success', email: data.email, error: null });
        } else {
          setState({ status: 'error', email: null, error: data.error || 'unknown' });
        }
      } catch {
        setState({ status: 'error', email: null, error: 'network' });
      }
    })();
  }, [searchParams]);

  const S = {
    wrap: {
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, background: '#FAF8F5', fontFamily: "'Inter', -apple-system, sans-serif",
    },
    card: {
      background: '#FFFFFF', border: '1px solid rgba(24,22,15,0.1)', borderRadius: 16,
      padding: '34px 28px', maxWidth: 460, width: '100%', textAlign: 'center',
      boxShadow: '0 12px 40px rgba(24,22,15,0.10)',
    },
    icon: { fontSize: 40, lineHeight: 1, marginBottom: 14 },
    title: { fontSize: 20, fontWeight: 800, color: '#18160F', margin: '0 0 10px' },
    text: { fontSize: 14.5, lineHeight: 1.6, color: '#55524A', margin: '0 0 22px' },
    mail: { fontWeight: 700, color: '#18160F', wordBreak: 'break-all' },
    btn: {
      background: '#E8533A', color: '#FFF', border: 'none', borderRadius: 999,
      padding: '12px 26px', fontSize: 15, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
    },
  };

  if (state.status === 'loading') {
    return (
      <div style={S.wrap}><div style={S.card}>
        <div style={S.icon}>⏳</div>
        <h1 style={S.title}>{t('auth:confirm_email.loading_title')}</h1>
      </div></div>
    );
  }

  if (state.status === 'success') {
    return (
      <div style={S.wrap}><div style={S.card}>
        <div style={S.icon}>✅</div>
        <h1 style={S.title}>{t('auth:confirm_email.success_title')}</h1>
        <p style={S.text}>
          {t('auth:confirm_email.success_text')}<br />
          <span style={S.mail}>{state.email}</span>
        </p>
        <button type="button" style={S.btn} onClick={() => navigate('/login')}>
          {t('auth:confirm_email.success_cta')}
        </button>
      </div></div>
    );
  }

  const errorKey = state.error === 'invalid_or_expired_link' ? 'expired'
    : state.error === 'email_already_used_other' ? 'taken'
    : 'generic';

  return (
    <div style={S.wrap}><div style={S.card}>
      <div style={S.icon}>⚠️</div>
      <h1 style={S.title}>{t(`auth:confirm_email.error_${errorKey}_title`)}</h1>
      <p style={S.text}>{t(`auth:confirm_email.error_${errorKey}_text`)}</p>
      <button type="button" style={S.btn} onClick={() => navigate('/compte')}>
        {t('auth:confirm_email.error_cta')}
      </button>
    </div></div>
  );
}
